import { useEffect, useCallback, useMemo } from "react";
import { useParams, Navigate } from "react-router-dom";
import { OuterContentContainer } from "@themes/pollinator/ui/OuterContentContainer";
import { AppPageHeader } from "@themes/pollinator/ui/AppPageHeader";
import { Loader } from "@themes/pollinator/ui/Loader";
import {
  useFetchActions,
  useUpdateActionStatus,
  getAction,
  shouldUpdateStatus,
  ACTION_TYPE_SLUG,
  ACTION_STATUS,
} from "@core/features/actions";
import { usePlatformStore } from "@platform/store";
import {
  ProgramWorkbookDownloadContainer,
  ProgramWelcomeKitContainer,
} from "@platform/features/programResources";

export function ResourcesPage() {
  const { id: programId } = useParams<{ id: string }>();
  const user = usePlatformStore((s) => s.user);

  const { data: actionsData, isLoading } = useFetchActions({
    programId: Number(programId),
    user,
    isEnabled: !!user && !!programId,
  });

  const workbookAction = useMemo(
    () =>
      actionsData?.actions
        ? getAction(actionsData.actions, ACTION_TYPE_SLUG.WORKBOOK_DOWNLOAD)
        : undefined,
    [actionsData?.actions]
  );

  const socialMediaKitAction = useMemo(
    () =>
      actionsData?.actions
        ? getAction(actionsData.actions, ACTION_TYPE_SLUG.SOCIALMEDIAKIT_DOWNLOAD)
        : undefined,
    [actionsData?.actions]
  );

  // --- Workbook action tracking ---
  const { mutate: updateWorkbookStatus } = useUpdateActionStatus({
    actionId: workbookAction?.id ?? 0,
    status: ACTION_STATUS.IN_PROGRESS,
  });

  useEffect(() => {
    if (
      workbookAction &&
      shouldUpdateStatus(workbookAction, ACTION_STATUS.IN_PROGRESS)
    ) {
      updateWorkbookStatus();
    }
  }, [workbookAction, updateWorkbookStatus]);

  const handleWorkbookDownload = useCallback(() => {
    if (
      workbookAction &&
      shouldUpdateStatus(workbookAction, ACTION_STATUS.COMPLETED)
    ) {
      updateWorkbookStatus({ status: ACTION_STATUS.COMPLETED });
    }
  }, [workbookAction, updateWorkbookStatus]);

  // --- Social media kit action tracking ---
  const { mutate: updateSocialMediaKitStatus } = useUpdateActionStatus({
    actionId: socialMediaKitAction?.id ?? 0,
    status: ACTION_STATUS.IN_PROGRESS,
  });

  useEffect(() => {
    if (
      socialMediaKitAction &&
      shouldUpdateStatus(socialMediaKitAction, ACTION_STATUS.IN_PROGRESS)
    ) {
      updateSocialMediaKitStatus();
    }
  }, [socialMediaKitAction, updateSocialMediaKitStatus]);

  const handleSocialMediaKitDownload = useCallback(() => {
    if (
      socialMediaKitAction &&
      shouldUpdateStatus(socialMediaKitAction, ACTION_STATUS.COMPLETED)
    ) {
      updateSocialMediaKitStatus({ status: ACTION_STATUS.COMPLETED });
    }
  }, [socialMediaKitAction, updateSocialMediaKitStatus]);

  // --- Access control ---
  const hasAnyResourceAction = !!workbookAction || !!socialMediaKitAction;

  if (isLoading) {
    return <Loader isFullPage />;
  }

  if (!hasAnyResourceAction) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <>
      <AppPageHeader
        title="Promotional Resources"
        description="Download program materials and access social media resources."
      />
      <OuterContentContainer className="flex flex-1" innerClassName="gap-8">
        <div className="w-full flex flex-col gap-6 pb-8">
          {workbookAction && (
            <ProgramWorkbookDownloadContainer onDownload={handleWorkbookDownload} />
          )}
          {socialMediaKitAction && (
            <ProgramWelcomeKitContainer onDownload={handleSocialMediaKitDownload} />
          )}
        </div>
      </OuterContentContainer>
    </>
  );
}

export default ResourcesPage;
